/* eslint-disable @typescript-eslint/no-explicit-any */
import { find, findLast, includes, isEmpty, some } from 'es-toolkit/compat';
import PhoneCountryCodes from '../constants/PhoneCountryCodes';

const COUNTRY_CODE_AND_REGION_CODE_ISO_REGEX = new RegExp(/\+([0-9]+) ([a-zA-Z]+)/);
const DEFAULT_COUNTRY_DISPLAY_STRING = '+1 US';

interface Chunk {
  start: number;
  end: number;
}
// custom highlighter function for phone results, used in conjunction with
// react-highlight-words
// the typings are incorrect but are to match what is coming from definitely typed
export function highlightPhone({
  autoEscape,
  caseSensitive,
  sanitize,
  searchWords,
  textToHighlight,
}: any): any {
  const chunks: Chunk[] = [];

  // Add chunks for every searchWord
  searchWords
    .filter((sw: string) => !isEmpty(sw))
    .forEach((sw: string) => {
      // case 1, a phone number has some separators like dot, dash
      // text to highlight is
      // +1 555-555-1212
      // and search is
      // 555.555.1212, or 555-555-1212
      const includesDash = includes(sw, '-');
      const includesDot = includes(sw, '.');
      if (includesDash || includesDot) {
        let splitSw: string[] = [];

        if (includesDash) {
          splitSw = sw.split('-');
        }

        if (includesDot) {
          splitSw = sw.split('.');
        }

        if (!isEmpty(splitSw)) {
          splitSw.forEach((sw: string) => {
            const idx = textToHighlight.indexOf(sw);
            if (idx !== -1) {
              chunks.push({
                start: idx,
                end: idx + sw.length,
              });
            }
          });
          return;
        }
      }
      // case 2, spaces
      // text to highlight is
      // +1 773-988-9390
      // search is 773 988
      const idx = textToHighlight.indexOf(sw);
      if (idx !== -1) {
        chunks.push({
          start: idx,
          end: idx + sw.length,
        });
      }
    });

  return chunks;
}

export const hasAnyMatch = (strings: string[], searchTerm: string): boolean => {
  return some(strings, (str: string) => includes(str, searchTerm));
};

export const hasAnyPhoneMatch = (phones: string[], searchTerms: string[]): boolean => {
  return some(searchTerms, (term) => {
    const includesDash = includes(term, '-');
    const includesDot = includes(term, '.');
    if (includesDash || includesDot) {
      let splitTerm: string[] = [];

      if (includesDash) {
        splitTerm = term.split('-');
      }

      if (includesDot) {
        splitTerm = term.split('.');
      }
      return some(splitTerm, (term) => hasAnyMatch(phones, term));
    }
    return hasAnyMatch(phones, term);
  });
};

export function getOrDefaultCountryCodeStringFromIsoCountry(isoCountry: string | undefined) {
  if (isoCountry === undefined) {
    return DEFAULT_COUNTRY_DISPLAY_STRING;
  }

  const defaultPhoneCountryCode = find(PhoneCountryCodes, (code) => code.iso === isoCountry);
  return defaultPhoneCountryCode
    ? `${defaultPhoneCountryCode.phone} ${defaultPhoneCountryCode.iso}`
    : DEFAULT_COUNTRY_DISPLAY_STRING;
}

export function getOrDefaultCountryCodeStringFromPrefix(countryCode: string | undefined) {
  if (countryCode) {
    const entryInCodesTable = findLast(PhoneCountryCodes, (code) => code.phone === countryCode);
    if (entryInCodesTable) {
      return `${entryInCodesTable.phone} ${entryInCodesTable.iso}`;
    }
  }
  // And a fallback return, in the event that countryCode is not defined or wasn't found
  // in the table:
  return DEFAULT_COUNTRY_DISPLAY_STRING;
}

export function getPhoneCountryComponents(isoPhoneCountryCode: string) {
  const matches = isoPhoneCountryCode.match(COUNTRY_CODE_AND_REGION_CODE_ISO_REGEX);

  if (!matches?.length || matches.length < 2) {
    return undefined;
  }

  const countryCode = matches[0] && matches[1] ? matches[1] : undefined;
  const regionCodeIso = matches[0] && matches ? matches[2] : undefined;

  if (!countryCode || !regionCodeIso) {
    return undefined;
  }

  return { countryCode, regionCodeIso };
}

export function getCountryCodeKeyFromPhoneCountryComponents({
  countryCode,
  regionCodeIso,
}: {
  countryCode?: string;
  regionCodeIso?: string;
}) {
  return countryCode && regionCodeIso
    ? `+${countryCode} ${regionCodeIso}`
    : getOrDefaultCountryCodeStringFromPrefix(`+${countryCode}`);
}

export function getBeautifiedPhoneNumber(beautifiedPhone: string) {
  if (beautifiedPhone.length === 11 && beautifiedPhone[0] === '1') {
    beautifiedPhone = beautifiedPhone.replace(/(\d{1})(\d{3})(\d{3})(\d+)/, '$1 $2-$3-$4');
  } else {
    beautifiedPhone = beautifiedPhone.replace(/(\d{3})(\d{3})(\d+)/, '$1-$2-$3');
  }
  return beautifiedPhone;
}
